import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react-native';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';

export const useRealtimeRecommendations = (session, generationStrategy = 'stream') => {
  const [recommendations, setRecommendations] = useState([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [error, setError] = useState(null);
  const [loadingRationale, setLoadingRationale] = useState(null);
  const [isBatchReady, setIsBatchReady] = useState(false);
  const [loadingType, setLoadingType] = useState('loading'); // 'loading' | 'generating'
  const [isLoadingTypeDetermined, setIsLoadingTypeDetermined] = useState(false);
  const timeoutRef = useRef(null);
  const channelRef = useRef(null);
  const initialDataFetchedRef = useRef(false); // Track if we've already fetched data immediately
  const initialDataCompleteRef = useRef(false); // Track if immediate data fetch returned complete data

  // Helper to detect if an error is an auth/JWT error
  const isAuthError = useCallback((err) => {
    if (!err) return false;
    const errorMessage = err.message || err.toString();
    return (
      errorMessage.includes('JWT') ||
      errorMessage.includes('Token has expired') ||
      errorMessage.includes('Invalid token') ||
      err.code === 'PGRST301' || // PostgREST JWT error
      err.code === '401'
    );
  }, []);

  const formatPayload = useCallback((record) => {
    if (!record) return null;

    const vehicleData = record.vehicle_models;

    // Log warning if vehicle_models join failed
    if (!vehicleData) {
      Sentry.captureException(
        new Error('Recommendation missing vehicle_models data'),
        {
          level: 'warning',
          tags: {
            component: 'useRealtimeRecommendations',
            error_type: 'data_integrity',
          },
          contexts: {
            recommendation: {
              userModelFitScoreId: record.user_model_fit_score_id,
              modelId: record.model_id,
              dataFidelity: record.data_fidelity,
            },
          },
        }
      );
    }

    // Log warning if model_id is missing
    if (!vehicleData?.model_id) {
      Sentry.captureException(
        new Error('Recommendation missing model_id'),
        {
          level: 'warning',
          tags: {
            component: 'useRealtimeRecommendations',
            error_type: 'data_integrity',
          },
          contexts: {
            recommendation: {
              userModelFitScoreId: record.user_model_fit_score_id,
              hasVehicleData: !!vehicleData,
              dataFidelity: record.data_fidelity,
            },
          },
        }
      );
    }

    // Find the default display image, or fallback to first image
    const primaryImage =
      vehicleData?.vehicle_model_images?.find((img) => img.is_default_display) ||
      vehicleData?.vehicle_model_images?.[0];

    return {
      userModelFitScoreId: record.user_model_fit_score_id,
      modelId: vehicleData?.model_id,
      make: vehicleData?.make,
      model: vehicleData?.model,
      trim: vehicleData?.trim,
      yearRange: vehicleData?.year_range,
      shortDescription: vehicleData?.short_description,
      personalized_rationale: record.personalized_rationale,
      generic_rationale: record.generic_rationale,
      // FIX: Use proper fallback logic - check user price range first, then model price range
      estimatedPriceRange: record.estimated_price_range || vehicleData?.estimated_price_range,
      monthlyOwnershipCost: vehicleData?.monthly_ownership_cost,
      depreciationInsight: vehicleData?.depreciation_insight,
      primaryImageUrl: primaryImage?.image_url || record.placeholder_image_url,
      scoreValue: record.score_value,
      scoreDetailsExplanations: record.score_details?.explanations,
      dataFidelity: record.data_fidelity,
      imageFidelity: record.image_fidelity,
      source: record.source,
      selected_for_feed: record.selected_for_feed,
      dormant_at: record.dormant_at,
    };
  }, []);

  const checkBatchReadiness = useCallback(
    (recs) => {
      if (generationStrategy !== 'batch') {
        setIsBatchReady(false);
        return false;
      }
      // Require at least 1 rec (deals users have 1-3, car-finder has 5)
      // and all must have confirmed data fidelity
      const isReady =
        recs.length >= 1 &&
        recs.every(
          (r) => r.dataFidelity === 'confirmed' || r.dataFidelity === 'confirmed_essentials'
        );
      setIsBatchReady(isReady);
      return isReady;
    },
    [generationStrategy]
  );

  // Check if all data AND images are fully confirmed - used for unsubscribing
  // This is separate from checkBatchReadiness which only checks data_fidelity for dismissing loading screen
  const checkFullyComplete = useCallback((recs) => {
    return (
      recs.length >= 1 &&
      recs.every(
        (r) =>
          (r.dataFidelity === 'confirmed' || r.dataFidelity === 'confirmed_essentials') &&
          r.imageFidelity === 'confirmed'
      )
    );
  }, []);

  const fetchFullData = useCallback(async () => {
    if (!session?.user) return [];

    const { data, error: fetchError } = await supabase
      .from('user_model_fit_scores')
      .select(`*, vehicle_models(*, vehicle_model_images(image_url, is_default_display))`)
      .eq('user_id', session.user.id)
      .is('deleted_at', null)
      .order('score_value', { ascending: false });

    if (fetchError) {
      // Mark auth errors with a special flag so components can handle them
      if (isAuthError(fetchError)) {
        const authError = new Error(fetchError.message || 'Session expired. Please log in again.');
        authError.isAuthError = true;
        authError.originalError = fetchError;
        setError(authError);
      } else {
        setError(fetchError);
      }
      return [];
    }
    // Deduplicate recommendations based on user_model_fit_score_id
    const uniqueData = [];
    const seenIds = new Set();
    if (data) {
      for (const item of data) {
        if (!seenIds.has(item.user_model_fit_score_id)) {
          seenIds.add(item.user_model_fit_score_id);
          uniqueData.push(item);
        }
      }
    }
    return uniqueData;
  }, [session, isAuthError]);

  // Pre-check effect to determine loading type before main subscription
  useEffect(() => {
    if (!session?.user) {
      setIsLoadingTypeDetermined(true);
      return;
    }

    // Reset state when starting determination
    setIsLoadingTypeDetermined(false);

    // Quick count to determine loading type before showing modal
    const determineLoadingType = async () => {
      // Use GET with limit 1 instead of HEAD to avoid CORS/RLS issues with HEAD requests
      // Count is returned separately from data when using { count: 'exact' }
      const { count, error: countError } = await supabase
        .from('user_model_fit_scores')
        .select('user_model_fit_score_id', { count: 'exact' })
        .eq('user_id', session.user.id)
        .limit(1); // Only fetch 1 row for efficiency, count still reflects total

      if (!countError) {
        // If count is 0, we're generating. If > 0, we're loading existing data.
        setLoadingType(count === 0 ? 'generating' : 'loading');
      }
      // Mark as determined regardless of error (use default 'loading' on error)
      setIsLoadingTypeDetermined(true);
    };

    determineLoadingType();
  }, [session]);

  useEffect(() => {
    if (!session?.user) {
      setRecommendations([]);
      setIsLoadingInitial(false);
      setError(null);
      return;
    }

    // Reset state when starting new subscription
    setIsLoadingInitial(true);
    setError(null);
    setIsBatchReady(false);
    initialDataFetchedRef.current = false;
    initialDataCompleteRef.current = false;

    // OPTIMIZATION: Fetch initial data immediately instead of waiting for subscription
    // This reduces the time showing skeleton when navigating back to dashboard
    const fetchInitialDataImmediately = async () => {
      const initialData = await fetchFullData();
      if (initialData && initialData.length > 0) {
        const formattedData = initialData.map(formatPayload);
        setRecommendations(formattedData);
        initialDataFetchedRef.current = true;
        // Check if data is complete/ready
        // NOTE: We only check data_fidelity, not image_fidelity, to dismiss modal as soon as content is ready
        // Images can continue loading in the background after modal dismisses
        const isComplete =
          initialData.length >= 5 &&
          initialData.every(
            (rec) =>
              rec.data_fidelity === 'confirmed' || rec.data_fidelity === 'confirmed_essentials'
          );
        if (isComplete) {
          setIsLoadingInitial(false);
          initialDataCompleteRef.current = true; // Mark as complete synchronously
          if (generationStrategy === 'batch') {
            setIsBatchReady(true);
          }
        }
      }
    };

    fetchInitialDataImmediately();

    // Channel name is stable per user to allow proper cleanup
    const channelName = `user_recommendations:${session.user.id}`;

    // Check if channel already exists in Supabase client registry and remove it
    const existingChannels = supabase.getChannels();
    logger.log(
      `[useRealtimeRecommendations] Current channels: ${existingChannels.map((ch) => ch.topic).join(', ')}`
    );
    // Supabase prefixes channel topics with 'realtime:' internally
    const existingChannel = existingChannels.find((ch) => ch.topic === `realtime:${channelName}`);
    if (existingChannel) {
      logger.log(
        `[useRealtimeRecommendations] Found existing channel "${channelName}", state: ${existingChannel.state}, removing it`
      );
      supabase.removeChannel(existingChannel);
    }

    const channel = supabase.channel(channelName);
    channelRef.current = channel;

    const handleScoreUpdate = async (payload) => {
      // For batch, a simple re-fetch is cleaner on any relevant update.
      if (generationStrategy === 'batch') {
        const fullData = await fetchFullData();
        const formattedData = fullData.map(formatPayload);
        setRecommendations(formattedData);

        // Check if batch is ready (data_fidelity confirmed) to dismiss loading screen
        if (checkBatchReadiness(formattedData)) {
          setIsLoadingInitial(false);
          clearTimeout(timeoutRef.current);
        }

        // Only unsubscribe when BOTH data AND images are fully confirmed
        // This allows realtime image updates to continue after loading screen dismisses
        if (checkFullyComplete(formattedData)) {
          if (channelRef.current) {
            logger.log('All data and images confirmed via real-time update. Unsubscribing.');
            supabase.removeChannel(channelRef.current);
            channelRef.current = null;
          }
        }
        return;
      }

      // --- Stream strategy logic (preserved) ---
      clearTimeout(timeoutRef.current);
      if (isLoadingInitial) setIsLoadingInitial(false);

      const updatedData = payload.new;
      const eventType = payload.eventType;

      setRecommendations((currentRecs) => {
        const existingRec = currentRecs.find(
          (rec) => rec.userModelFitScoreId === updatedData.user_model_fit_score_id
        );
        const wasDataFidelityUpgraded =
          existingRec &&
          existingRec.dataFidelity !== 'confirmed' &&
          updatedData.data_fidelity === 'confirmed';
        const wasImageFidelityUpgraded =
          existingRec &&
          existingRec.imageFidelity !== 'confirmed' &&
          updatedData.image_fidelity === 'confirmed';

        if (eventType === 'INSERT' || wasDataFidelityUpgraded || wasImageFidelityUpgraded) {
          fetchFullData().then((fullData) => {
            const formattedData = fullData.map(formatPayload);
            setRecommendations(formattedData);
          });
          return currentRecs;
        }

        if (eventType === 'UPDATE') {
          const updatedList = currentRecs.map((rec) => {
            if (rec.userModelFitScoreId === updatedData.user_model_fit_score_id) {
              const newPriceRange =
                updatedData.estimated_price_range !== undefined &&
                updatedData.estimated_price_range !== null
                  ? updatedData.estimated_price_range
                  : rec.estimatedPriceRange;

              return {
                ...rec,
                personalized_rationale: updatedData.personalized_rationale,
                generic_rationale: updatedData.generic_rationale,
                scoreValue: updatedData.score_value,
                dataFidelity: updatedData.data_fidelity,
                imageFidelity: updatedData.image_fidelity,
                estimatedPriceRange: newPriceRange,
              };
            }
            return rec;
          });
          return updatedList.sort((a, b) => (b.scoreValue || 0) - (a.scoreValue || 0));
        }

        if (eventType === 'DELETE') {
          return currentRecs.filter(
            (rec) => rec.userModelFitScoreId !== payload.old.user_model_fit_score_id
          );
        }

        return currentRecs;
      });
    };

    channel
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_model_fit_scores',
          filter: `user_id=eq.${session.user.id}`,
        },
        handleScoreUpdate
      )
      .subscribe(async (status, err) => {
        if (err) {
          if (isAuthError(err)) {
            const authError = new Error(err.message || 'Session expired. Please log in again.');
            authError.isAuthError = true;
            authError.originalError = err;
            setError(authError);
          } else {
            setError(err);
          }
          setIsLoadingInitial(false);
          return;
        }

        if (status === 'SUBSCRIBED') {
          // Check if we already fetched data immediately AND it's complete to avoid duplicate work
          // If data was fetched but incomplete, we still need to set up the timeout
          // Use ref instead of state to avoid race conditions with async state updates
          if (initialDataFetchedRef.current && initialDataCompleteRef.current) {
            logger.log('Data already complete from immediate fetch, skipping duplicate work');
            return;
          }

          const initialData = await fetchFullData();

          // Determine loading type based on initial data
          const isGenerating =
            !initialData ||
            initialData.length === 0 ||
            initialData.every((rec) => rec.data_fidelity === 'placeholder');
          setLoadingType(isGenerating ? 'generating' : 'loading');

          if (generationStrategy === 'batch') {
            const formattedData = initialData.map(formatPayload);
            setRecommendations(formattedData);
            const isReady = checkBatchReadiness(formattedData);

            if (isReady) {
              setIsLoadingInitial(false);
              logger.log('Batch data ready on initial load (loading screen can dismiss).');

              // Only unsubscribe if images are also confirmed
              if (checkFullyComplete(formattedData)) {
                logger.log('All data and images confirmed on initial load. Unsubscribing.');
                supabase.removeChannel(channel);
                channelRef.current = null;
              } else {
                logger.log('Images still pending. Keeping subscription for realtime image updates.');
              }
            } else {
              // Batch is not ready, keep listening for updates.
              // LoadingScreen handles timeout logic (120s)
              logger.log(
                'Batch not ready on initial load. Listening for updates (LoadingScreen handles timeout).'
              );
            }
            return;
          }

          // Fallback stream logic (preserved)
          const isDataComplete =
            initialData &&
            initialData.length >= 5 &&
            initialData.every(
              (rec) => rec.data_fidelity === 'confirmed' && rec.image_fidelity === 'confirmed'
            );

          if (isDataComplete) {
            logger.log('All recommendations are complete. Unsubscribing from real-time channel.');
            setRecommendations(initialData.map(formatPayload));
            setIsLoadingInitial(false);
            supabase.removeChannel(channel);
            channelRef.current = null;
          } else if (initialData && initialData.length > 0) {
            if (initialData[0]?.generic_rationale) {
              setLoadingRationale(initialData[0].generic_rationale);
            }
            setRecommendations(initialData.map(formatPayload));
            setIsLoadingInitial(false);
          } else {
            // No recommendations yet, keep loading state (LoadingScreen handles timeout)
            setRecommendations([]);
          }
        }
      });

    return () => {
      clearTimeout(timeoutRef.current);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [
    session,
    fetchFullData,
    formatPayload,
    generationStrategy,
    checkBatchReadiness,
    checkFullyComplete,
    isAuthError,
    // isLoadingInitial intentionally excluded from dependencies to prevent effect re-run when loading state changes
  ]);

  return {
    recommendations,
    isLoadingInitial,
    error,
    loadingRationale,
    isBatchReady,
    loadingType,
    isLoadingTypeDetermined,
  };
};
